import { create } from 'zustand';
import { CustomProjectsGateway } from '../Processes/Gateways/CustomProjects/CustomProjectsGateway';
import { CustomProjectModel } from '@entities/CustomProject/CustomProjectModel';
import { CustomProjectEntity } from '@entities/CustomProject/Types';
import { PaginationResponse } from '@shared/Interfaces/Api/Pagination';
import { GetTemplateAndFragmentQuestion } from '@modules/Steps/Shared/Helpers/GetTemplateAndFragmentQuestion';
import { QuestionTypeEnum } from '@shared/Enums/QuestionTypeEnum';
import { RawItemType } from '@entities/ItemType/Types';
import { RawItem } from '@entities/Item/Types';

const projectGateway = new CustomProjectsGateway();

const customProjectModel = new CustomProjectModel();

interface AllItemsTypesItemsData {
    data: RawItem[];
    pagination: PaginationResponse;
}

export interface RawItemTypeWithItems extends Omit<RawItemType, 'items'> {
    items: AllItemsTypesItemsData;
}

export interface State {
    customProject: CustomProjectEntity;

    isProjectLoading: boolean;
    isProjectUpdateLoading: boolean;
    isMobile: boolean;
}

export interface Actions {
    setCustomProject: (newCustomProject: CustomProjectEntity) => void;
    setProjectLoading: (isLoading: boolean) => void;
    setProjectUpdateLoading: (isLoading: boolean) => void;
    updateQuestionInputValue: (questionUuid: string, newValue: string) => void;
    updateQuestionSelectValue: (
        questionUuid: string,
        answerUuid: string
    ) => Promise<boolean>;
    setIsMobile: (isMobile: boolean) => void;
}

export const useProjectStore = create<State & Actions>((set, get) => ({
    customProject: customProjectModel,
    customProjectItemTypes: [],
    isProjectLoading: false,
    isProjectUpdateLoading: false,
    isMobile: false,

    setCustomProject: (newCustomProject: CustomProjectEntity) => {
        set({ customProject: newCustomProject });
    },

    setProjectLoading: (isLoading: boolean) => {
        set({ isProjectLoading: isLoading });
    },

    setProjectUpdateLoading: (isLoading: boolean) => {
        set({ isProjectUpdateLoading: isLoading });
    },

    updateQuestionInputValue: (questionUuid: string, newValue: string) => {
        const { customProject } = get();

        const question = GetTemplateAndFragmentQuestion(
            customProject,
            questionUuid
        );

        if (!question || !customProject) {
            return;
        }

        let formattedValue = newValue;
        const parsedValue = parseFloat(newValue);

        if (!isNaN(parsedValue)) {
            if (newValue.includes('.') && newValue.split('.')[1]?.length > 2) {
                formattedValue = parsedValue.toFixed(2);
            }
        }

        question.updateInputValue(formattedValue);

        set({ customProject });
    },

    updateQuestionSelectValue: async (
        questionUuid: string,
        answerUuid: string
    ) => {
        const { customProject } = get();
        const question = GetTemplateAndFragmentQuestion(
            customProject,
            questionUuid
        );
        if (!question || !customProject) {
            return true;
        }

        if (question.select === QuestionTypeEnum.STYLE) {
            const response = await projectGateway.checkDisabledQuestion(
                customProject.id,
                answerUuid,
                question
            );

            const isDisabled = response.data?.disabled ?? false;

            if (!isDisabled) {
                question.updateSelectValue(answerUuid);

                set({ customProject });
            }

            return isDisabled;
        }

        question.updateSelectValue(answerUuid);

        set({ customProject });
        return false;
    },

    setIsMobile: (isMobile: boolean) => {
        set({ isMobile });
    },
}));
